import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Mail, MapPin, FileText, CheckCircle, Clock, Eye, Upload, Send, Printer } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerDetail({ token, user, onLogout }) {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleBackToList = () => {
    // Navigate back to customers list with scroll target
    navigate('/customers', { state: { scrollToCustomerId: customerId } });
  };

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      setIsLoading(true);
      // Fetch customer
      const customerRes = await axios.get(`${API}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(customerRes.data);

      // Fetch delivery notes for this customer
      const notesRes = await axios.get(`${API}/delivery-notes?customer_id=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryNotes(notesRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kundendaten');
      navigate('/customers');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleEmailSetting = async (newValue) => {
    try {
      await axios.put(
        `${API}/customers/${customerId}`,
        { send_email_on_delivery: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomer({ ...customer, send_email_on_delivery: newValue });
      toast.success(newValue ? 'E-Mail-Versand aktiviert' : 'E-Mail-Versand deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Einstellung');
    }
  };

  const handleTogglePaperSetting = async (newValue) => {
    try {
      await axios.put(
        `${API}/customers/${customerId}`,
        { requires_paper_delivery_note: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomer({ ...customer, requires_paper_delivery_note: newValue });
      toast.success(newValue ? 'Papier-Lieferschein erforderlich' : 'Papier-Lieferschein nicht erforderlich');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Einstellung');
    }
  };

  const signedNotes = deliveryNotes.filter(note => note.signed);
  const unsignedNotes = deliveryNotes.filter(note => !note.signed);

  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Kundendaten...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="customer-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={handleBackToList}
              data-testid="back-to-customers-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
            <div>
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>
                {customer.company_name}
              </h1>
              <p className="text-gray-600 mt-1">Kundendetails und Lieferscheine</p>
            </div>
          </div>
          <Button
            className="metaxon-primary text-white"
            onClick={() => navigate(`/delivery-notes?customer=${customerId}`)}
            data-testid="upload-for-customer-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Lieferschein hochladen
          </Button>
        </div>

        {/* Customer Logo Card - Large Display */}
        {customer.logo_data && (
          <Card className="glass-effect border-0">
            <CardContent className="pt-6">
              <div className="flex justify-center items-center p-8 bg-white rounded-lg border border-gray-100">
                <img 
                  src={customer.logo_data} 
                  alt={`${customer.company_name} Logo`}
                  className="max-h-48 max-w-full object-contain"
                  style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Info Card */}
        <Card className="glass-effect border-0">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Kundendaten</CardTitle>
                {customer.customer_id && (
                  <CardDescription>Kunden-ID: {customer.customer_id}</CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {customer.street && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Adresse</p>
                    <p className="font-medium">{customer.street}</p>
                    <p className="font-medium">
                      {customer.zipcode} {customer.city}
                    </p>
                    {customer.country && <p className="text-gray-500">{customer.country}</p>}
                  </div>
                </div>
              )}
              {customer.email && (
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">E-Mail</p>
                    <p className="font-medium text-blue-600">{customer.email}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Verpackungsmaterial-Konto */}
            {((customer.palette_balance || 0) > 0 || (customer.eurobox_balance || 0) > 0 || (customer.aufsatzrahmen_balance || 0) > 0) && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-900 mb-3">📦 Verpackungsmaterial-Konto</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{customer.palette_balance || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Paletten</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{customer.aufsatzrahmen_balance || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Aufsatzrahmen</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{customer.eurobox_balance || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Euroboxen</p>
                  </div>
                </div>
              </div>
            )}

            {/* Lieferschein-Einstellungen */}
            <div className="mt-6 space-y-4">
              <h4 className="font-semibold text-gray-900 mb-3">📄 Lieferschein-Einstellungen</h4>
              
              <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3 flex-1">
                  <Send className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <Label className="text-base font-medium">E-Mail-Versand nach Unterschrift</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Lieferschein wird automatisch per E-Mail an den Kunden verschickt
                    </p>
                  </div>
                </div>
                <Switch
                  checked={customer.send_email_on_delivery !== false}
                  onCheckedChange={handleToggleEmailSetting}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3 flex-1">
                  <Printer className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <Label className="text-base font-medium">Papier-Lieferschein erforderlich</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Kunde benötigt einen gedruckten Lieferschein
                    </p>
                  </div>
                </div>
                <Switch
                  checked={customer.requires_paper_delivery_note === true}
                  onCheckedChange={handleTogglePaperSetting}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Notes Tabs */}
        <Card className="glass-effect border-0">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Lieferscheine ({deliveryNotes.length})
            </CardTitle>
            <CardDescription>
              {signedNotes.length} unterschrieben, {unsignedNotes.length} ausstehend
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">
                  <FileText className="mr-2 h-4 w-4" />
                  Übersicht
                </TabsTrigger>
                <TabsTrigger value="unsigned" data-testid="tab-unsigned">
                  <Clock className="mr-2 h-4 w-4" />
                  Ausstehend ({unsignedNotes.length})
                </TabsTrigger>
                <TabsTrigger value="signed" data-testid="tab-signed">
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Unterschrieben ({signedNotes.length})
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab with Table */}
              <TabsContent value="overview" className="mt-6">
                {deliveryNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Keine Lieferscheine vorhanden</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-2 font-semibold">Projektnummer</th>
                          <th className="text-left py-3 px-2 font-semibold">Datum</th>
                          <th className="text-left py-3 px-2 font-semibold">Liefermethode</th>
                          <th className="text-right py-3 px-2 font-semibold">Versandkosten</th>
                          <th className="text-center py-3 px-2 font-semibold">Paletten</th>
                          <th className="text-center py-3 px-2 font-semibold">Aufsatzrahmen</th>
                          <th className="text-center py-3 px-2 font-semibold">Euroboxen</th>
                          <th className="text-center py-3 px-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryNotes.map((note) => {
                          const deliveryInfo = note.delivery_info || {};
                          const isPalette = deliveryInfo.packaging_type === 'palette';
                          const isEurobox = deliveryInfo.packaging_type === 'eurobox';
                          const isExchange = deliveryInfo.is_exchange || false;
                          
                          // Handle new format (palette with rahmen as object) and old format (single number)
                          let paletteCount = 0;
                          let rahmenCount = 0;
                          let euroboxCount = 0;
                          
                          if (isPalette) {
                            if (typeof deliveryInfo.packaging_count === 'object' && deliveryInfo.packaging_count !== null) {
                              paletteCount = deliveryInfo.packaging_count.paletten || 0;
                              rahmenCount = deliveryInfo.packaging_count.rahmen || 0;
                            } else {
                              paletteCount = deliveryInfo.packaging_count || 0;
                            }
                          } else if (isEurobox) {
                            euroboxCount = deliveryInfo.packaging_count || 0;
                          }

                          return (
                            <tr key={note.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-2">
                                <span className="font-medium text-blue-600">
                                  {note.project_number || 'N/A'}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                {note.delivery_date || new Date(note.uploaded_at).toLocaleDateString('de-DE')}
                              </td>
                              <td className="py-3 px-2">
                                {deliveryInfo.delivery_method === 'abholung' && '🚶 Abholung'}
                                {deliveryInfo.delivery_method === 'metaxon' && '🏢 Metaxon AG'}
                                {deliveryInfo.delivery_method === 'paketdienst' && (
                                  <span>📦 {deliveryInfo.courier_service || 'Paketdienst'}</span>
                                )}
                                {deliveryInfo.delivery_method === 'fahrdienst' && '🚚 Fahrdienst'}
                                {!deliveryInfo.delivery_method && '-'}
                              </td>
                              <td className="py-3 px-2 text-right">
                                {deliveryInfo.courier_cost ? (
                                  <span className="font-semibold text-green-600">
                                    {deliveryInfo.courier_cost.toFixed(2)} €
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-2 text-center">
                                {isPalette && paletteCount > 0 && !isExchange ? (
                                  <span className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold">
                                    {paletteCount}
                                  </span>
                                ) : isPalette && paletteCount > 0 && isExchange ? (
                                  <span className="text-gray-400">↔️ {paletteCount}</span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-2 text-center">
                                {isPalette && rahmenCount > 0 && !isExchange ? (
                                  <span className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold">
                                    {rahmenCount}
                                  </span>
                                ) : isPalette && rahmenCount > 0 && isExchange ? (
                                  <span className="text-gray-400">↔️ {rahmenCount}</span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-2 text-center">
                                {isEurobox && euroboxCount > 0 && !isExchange ? (
                                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">
                                    {euroboxCount}
                                  </span>
                                ) : isEurobox && euroboxCount > 0 && isExchange ? (
                                  <span className="text-gray-400">↔️ {euroboxCount}</span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-2 text-center">
                                {note.signed ? (
                                  <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                    ✓ Unterschrieben
                                  </span>
                                ) : (
                                  <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                                    Ausstehend
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Summary Row */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div className="font-semibold">Gesamt:</div>
                        <div className="flex gap-6">
                          <div className="text-center">
                            <p className="text-lg font-bold text-amber-600">
                              {deliveryNotes.reduce((sum, note) => {
                                const info = note.delivery_info || {};
                                if (info.packaging_type === 'palette' && !info.is_exchange) {
                                  if (typeof info.packaging_count === 'object' && info.packaging_count !== null) {
                                    return sum + (info.packaging_count.paletten || 0);
                                  }
                                  return sum + (info.packaging_count || 0);
                                }
                                return sum;
                              }, 0)}
                            </p>
                            <p className="text-xs text-gray-600">Paletten</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-amber-600">
                              {deliveryNotes.reduce((sum, note) => {
                                const info = note.delivery_info || {};
                                if (info.packaging_type === 'palette' && !info.is_exchange) {
                                  if (typeof info.packaging_count === 'object' && info.packaging_count !== null) {
                                    return sum + (info.packaging_count.rahmen || 0);
                                  }
                                }
                                return sum;
                              }, 0)}
                            </p>
                            <p className="text-xs text-gray-600">Aufsatzrahmen</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-blue-600">
                              {deliveryNotes.reduce((sum, note) => {
                                const info = note.delivery_info || {};
                                if (info.packaging_type === 'eurobox' && !info.is_exchange) {
                                  return sum + (info.packaging_count || 0);
                                }
                                return sum;
                              }, 0)}
                            </p>
                            <p className="text-xs text-gray-600">Euroboxen</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600">
                              {deliveryNotes.reduce((sum, note) => {
                                const info = note.delivery_info || {};
                                return sum + (info.courier_cost || 0);
                              }, 0).toFixed(2)} €
                            </p>
                            <p className="text-xs text-gray-600">Versandkosten</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="unsigned" className="mt-6">
                {unsignedNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Keine ausstehenden Lieferscheine</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {unsignedNotes.map((note) => (
                      <Card key={note.id} className="border hover:shadow-md transition-shadow" data-testid={`unsigned-note-${note.id}`}>
                        <CardHeader>
                          <div className="flex items-center space-x-2">
                            <div className="p-2 rounded-lg bg-orange-100">
                              <Clock className="h-4 w-4 text-orange-600" />
                            </div>
                            <CardTitle className="text-sm">{note.filename}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-xs text-gray-600 mb-3">
                            Hochgeladen: {formatDate(note.uploaded_at)}
                          </p>
                          <Button
                            className="w-full metaxon-primary text-white"
                            size="sm"
                            onClick={() => navigate(`/sign/${note.id}`)}
                            data-testid={`sign-note-btn-${note.id}`}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Unterschreiben
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="signed" className="mt-6">
                {signedNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Noch keine unterschriebenen Lieferscheine</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {signedNotes.map((note) => (
                      <Card key={note.id} className="border hover:shadow-md transition-shadow" data-testid={`signed-note-${note.id}`}>
                        <CardHeader>
                          <div className="flex items-center space-x-2">
                            <div className="p-2 rounded-lg bg-green-100">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </div>
                            <CardTitle className="text-sm">{note.filename}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 text-xs">
                          <div>
                            <span className="text-gray-600">Hochgeladen:</span>
                            <p>{formatDate(note.uploaded_at)}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Unterschrieben:</span>
                            <p>{formatDate(note.signed_at)}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Von:</span>
                            <p className="font-medium">{note.signer_name}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
